import * as React from 'react';
import type { SharedSelectOption, setState } from '@types';
import { CheckIcon, ChevronDown, X } from 'lucide-react';
import { cn } from '@core-ui-lib/utils';
import PropTypes from 'prop-types';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@core-ui/Components/ui/popover';

import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@core-ui/Components/ui/command';
import { useTranslation } from 'react-i18next';

const TriggerButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    {...props}
    className={cn(
      'flex h-8 w-full items-center justify-between rounded-md border px-2',
      'bg-background text-foreground border-border hover:bg-muted/50 transition-colors',
      props.disabled === true && 'opacity-50 cursor-not-allowed',
      className
    )}
  />
));
TriggerButton.displayName = 'TriggerButton';
TriggerButton.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

interface InputSelectTriggerProps {
  options: SharedSelectOption[];
  placeholder: string;
  clearable: boolean;
  disabled: boolean;
  isPopoverOpen: boolean;
  selectedValue: string;
  setIsPopoverOpen: (v: boolean) => void;
  onClearAllOptions: () => void;
  className?: string;
  children?: (v: SharedSelectOption) => React.ReactNode;
  style?: React.CSSProperties;
}

export const InputSelectTrigger = React.forwardRef<
  HTMLButtonElement,
  InputSelectTriggerProps
>(function InputSelectTrigger (
  {
    options,
    placeholder,
    clearable,
    disabled,
    selectedValue,
    isPopoverOpen,
    setIsPopoverOpen,
    onClearAllOptions,
    className,
    style,
    children,
  },
  ref
) {
  return (
    <TriggerButton
      ref={ref}
      type="button"
      disabled={disabled}
      onClick={() => {
        setIsPopoverOpen(!isPopoverOpen);
      }}
      className={className}
      style={style}
    >
      {(selectedValue !== '') ? (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2 px-1 min-w-0">
            {(() => {
              const opt = options.find((o) => o.value === selectedValue);
              if (opt == null) {
                return null;
              }
              return (children != null) ? (
                children(opt)
              ) : (
                <>
                  {(opt.icon != null) && (
                    <opt.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="truncate">{opt.label}</span>
                </>
              );
            })()}
          </div>
          <div className="flex items-center">
            {clearable && (
              <>
                <X
                  className="mx-2 h-4 w-4 cursor-pointer text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClearAllOptions();
                  }}
                />
                <span className="mx-1 h-6 w-px bg-border" />
              </>
            )}
            <ChevronDown
              className={cn(
                'mx-2 h-4 w-4 text-muted-foreground transition-transform',
                isPopoverOpen && 'rotate-180'
              )}
            />
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between w-full">
          <span className="mx-2 text-sm text-muted-foreground truncate">
            {placeholder}
          </span>
          <ChevronDown
            className={cn(
              'mx-2 h-4 w-4 text-muted-foreground transition-transform',
              isPopoverOpen && 'rotate-180'
            )}
          />
        </div>
      )}
    </TriggerButton>
  );
});

export interface InputSelectProvided {
  options: SharedSelectOption[];
  onValueChange?: (v: string) => void;
  placeholder: string;
  clearable: boolean;
  disabled: boolean;
  selectedValue: string;
  setSelectedValue: setState<string>;
  isPopoverOpen: boolean;
  setIsPopoverOpen: setState<boolean>;
  onOptionSelect: (v: string) => void;
  onClearAllOptions: () => void;
}

export const InputSelect = React.forwardRef<
  HTMLDivElement,
  {
    options: SharedSelectOption[];
    value?: string;
    onValueChange?: (v: string) => void;
    placeholder?: string;
    clearable?: boolean;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
    children: (v: InputSelectProvided) => React.ReactNode;
      }
      >(function InputSelect (
        {
          options,
          value = '',
          onValueChange,
          placeholder = 'Select...',
          clearable = false,
          disabled = false,
          className,
          children,
          ...rest
        },
        _ref
      ) {
        const [selectedValue, setSelectedValue] = React.useState(value);
        const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
        const { t } = useTranslation();

        const onOptionSelect = (v: string): void => {
          setSelectedValue(v);
          onValueChange?.(v);
          setIsPopoverOpen(false);
        };

        const onClearAllOptions = (): void => {
          setSelectedValue('');
          onValueChange?.('');
          setIsPopoverOpen(false);
        };

        // Sync internal state when external value changes
        React.useEffect(() => {
          setSelectedValue(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [value]);

        return (
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={false}>
            <PopoverTrigger asChild>
              {children({
                options,
                onValueChange,
                placeholder,
                clearable,
                disabled,
                selectedValue,
                setSelectedValue,
                isPopoverOpen,
                setIsPopoverOpen,
                onOptionSelect,
                onClearAllOptions,
              })}
            </PopoverTrigger>

            <PopoverContent
              align="start"
              side="bottom"
              sideOffset={6}
              collisionPadding={8}
              className={cn(
                'w-[var(--radix-popover-trigger-width)] p-0 rounded-lg border bg-card text-foreground border-border shadow-xl',
                'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                className
              )}
              onOpenAutoFocus={(e) => {
                e.preventDefault();
              }}
              onEscapeKeyDown={() => {
                setIsPopoverOpen(false);
              }}
              {...rest}
            >
              <Command>
                <div className="sticky top-0 z-10 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
                  <div className="px-5 py-2">
                    <CommandInput
                      placeholder= {t('customSelect.search', 'Search...')}
                      className={cn(
                        'rounded-md border border-border bg-background',
                        'pl-8 pr-3 text-sm placeholder:text-muted-foreground',
                        'focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary/50'
                      )}
                    />
                  </div>
                  <div className="border-b border-border" />
                </div>

                <div
                  className="max-h-80 overflow-y-auto overscroll-contain [scrollbar-gutter:stable] px-1 py-1"
                  onWheelCapture={(e) => {
                    e.stopPropagation();
                  }}
                  onTouchMoveCapture={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <CommandList className="max-h-none">
                    <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
                No results
                    </CommandEmpty>

                    <CommandGroup className="p-1">
                      {options.map((opt) => {
                        const active = selectedValue === opt.value;
                        return (
                          <CommandItem
                            key={opt.value}
                            onSelect={() => {
                              onOptionSelect(opt.value);
                            }}
                            className="group cursor-pointer rounded-md px-3 py-2 aria-selected:bg-muted/60 focus:bg-muted/60"
                          >
                            <div
                              className={cn(
                                'mr-2 flex h-4 w-4 shrink-0 items-center justify-center',
                                active ? 'text-primary' : 'opacity-0 group-hover:opacity-40'
                              )}
                            >
                              <CheckIcon className="h-4 w-4" />
                            </div>
                            {(opt.icon != null) && (
                              <opt.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="truncate">{opt.label}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </div>

                <CommandSeparator />
                <div className="p-2">
                  <button
                    data-qa-id="close-button"
                    type="button"
                    onClick={() => {
                      setIsPopoverOpen(false);
                    }}
                    className="w-full text-xs text-muted-foreground hover:text-foreground transition"
                  >
                    {t( 'customSelect.close', 'Close')}
                  </button>
                </div>
              </Command>
            </PopoverContent>
          </Popover>
        );
      });
